import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HeaderForm from "../../components/Header";
import Loader from "../../components/Loader";
import { errorMessage } from "../../components/Toaster";
import { formatDateToISOString } from "../../utils/utils";
import { getErrorMessage } from "../../utils/error";
import { setFilters } from "../../store/slices/filterSlice";
import {
  fetchWarehouseReportData,
  fetchWarehouseSessions,
} from "../../store/slices/reportSlice";
import {
  PlayCircle,
  X,
  FileText,
  ImageOff,
  ZoomIn,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { VISO_SUPER_ADMIN } from "../../constants";
import { getSessionSkusDetails } from "./api";
import { generateSessionReportPDF } from "../../components/Report";

const getDrivePreviewUrl = (url) => {
  if (!url) return null;
  const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (!match) return url;
  return `https://drive.google.com/file/d/${match[1]}/preview`;
};

const getDriveImageUrl = (url) => {
  if (!url) return null;
  const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (!match) return url;
  return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w200`;
};

const VideoModal = ({ videoPath, onClose }) => {
  const previewUrl = getDrivePreviewUrl(videoPath);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl overflow-hidden w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-medium text-sm">Session Video</span>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={18} />
          </button>
        </div>
        <div className="relative bg-black" style={{ height: "70vh" }}>
          {!iframeLoaded && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-sm gap-2">
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Loading video…</span>
            </div>
          )}
          <iframe
            src={previewUrl}
            title="Session video"
            className="w-full h-full"
            allow="autoplay; fullscreen"
            onLoad={() => setIframeLoaded(true)}
          />
        </div>
      </div>
    </div>
  );
};

const ImagePreviewModal = ({ imageUrl, skuName, onClose }) => (
  <div
    className="fixed inset-0 flex items-center justify-center p-4"
    style={{ background: "rgba(0,0,0,0.85)", zIndex: 80 }}
    onClick={onClose}
  >
    <div
      className="rounded-xl overflow-hidden inline-block"
      style={{
        background: "#fff",
        boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
        maxWidth: "90vw",
        maxHeight: "90vh",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200">
        <span className="text-sm font-semibold text-gray-900">{skuName}</span>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors flex-shrink-0"
        >
          <X size={18} />
        </button>
      </div>
      <div style={{ background: "#0f172a" }}>
        <img
          src={imageUrl}
          alt={skuName}
          style={{
            display: "block",
            maxWidth: "90vw",
            maxHeight: "calc(90vh - 56px)",
          }}
        />
      </div>
    </div>
  </div>
);

const SessionDetailModal = ({ session, onClose }) => {
  const [skuDetails, setSkuDetails] = useState([]);
  const [loadingSkus, setLoadingSkus] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingSkus(true);
    getSessionSkusDetails(session.session_id)
      .then((res) => {
        if (!cancelled && res.status && Array.isArray(res.data)) {
          setSkuDetails(res.data);
        }
      })
      .catch((err) => {
        errorMessage(getErrorMessage(err) || "Error fetching SKU breakdown.");
      })
      .finally(() => {
        if (!cancelled) setLoadingSkus(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session.session_id]);

  const formatTime = (t) =>
    t
      ? new Date(t).toLocaleString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : "-";

  const getDuration = () => {
    if (!session.start_time || !session.end_time) return "-";
    const ms = new Date(session.end_time) - new Date(session.start_time);
    const totalSec = Math.floor(ms / 1000);
    const h = String(Math.floor(totalSec / 3600)).padStart(2, "0");
    const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
    const s = String(totalSec % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  const Field = ({ label, children }) => (
    <div>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 500,
          letterSpacing: "0.04em",
          color: "#94a3b8",
          textTransform: "uppercase",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 500, color: "#1e293b" }}>{children}</div>
    </div>
  );

  const SkuThumbnail = ({ sku }) => {
    const [failed, setFailed] = useState(false);
    const src = getDriveImageUrl(sku.image_path);

    if (!src || failed) {
      return (
        <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
          <ImageOff size={14} className="text-gray-300" />
        </div>
      );
    }

    return (
      <button
        onClick={() => setPreviewImage({ url: src, name: sku.sku_name })}
        className="relative w-10 h-10 rounded overflow-hidden border border-gray-200 flex-shrink-0 group cursor-zoom-in"
        title="Click to preview"
      >
        <img
          src={src}
          alt={sku.sku_name}
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-colors">
          <ZoomIn size={14} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </button>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[88vh] overflow-y-auto"
        style={{
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #e2e8f0",
          boxShadow: "0 20px 50px rgba(15,23,42,0.25), 0 4px 14px rgba(15,23,42,0.1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <span className="font-semibold text-base text-gray-900">
            #{session.session_id} — {session.gate_name}
          </span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pt-5 grid grid-cols-4 gap-x-5 gap-y-5">
          <Field label="Gate">{session.gate_name}</Field>
          <Field label="Truck No.">{session.truck_number || "-"}</Field>
          <Field label="Task">
            <span style={{ color: "#2563eb" }}>{session.task || "-"}</span>
          </Field>
          <Field label="Status">
            <span
              className="capitalize"
              style={{
                display: "inline-block",
                fontSize: 11,
                fontWeight: 600,
                padding: "2px 10px",
                borderRadius: 999,
                background: session.status === "active" ? "#fee2e2" : "#f1f5f9",
                color: session.status === "active" ? "#dc2626" : "#475569",
              }}
            >
              {session.status}
            </span>
          </Field>
        </div>

        <div className="px-6 pt-5 pb-5 grid grid-cols-4 gap-x-5 gap-y-5 border-b border-gray-100">
          <Field label="Start">{formatTime(session.start_time)}</Field>
          <Field label="End">{formatTime(session.end_time)}</Field>
          <Field label="Duration">{getDuration()}</Field>
          <Field label="Total Cargo">{session.cargo_count}</Field>
        </div>

        <div className="px-6 py-5">
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 500,
              letterSpacing: "0.04em",
              color: "#94a3b8",
              textTransform: "uppercase",
              marginBottom: 10,
            }}
          >
            SKU Breakdown
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-4 py-2 text-left"
                    style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: "0.03em", color: "#94a3b8", textTransform: "uppercase" }}
                  >
                    Image
                  </th>
                  <th
                    className="px-4 py-2 text-left"
                    style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: "0.03em", color: "#94a3b8", textTransform: "uppercase" }}
                  >
                    SKU
                  </th>
                  <th
                    className="px-4 py-2 text-left"
                    style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: "0.03em", color: "#94a3b8", textTransform: "uppercase" }}
                  >
                    Count
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loadingSkus ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-400">
                      Loading…
                    </td>
                  </tr>
                ) : skuDetails.length > 0 ? (
                  skuDetails.map((sku) => (
                    <tr key={sku.sku_name}>
                      <td className="px-4 py-3">
                        <SkuThumbnail sku={sku} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800">{sku.sku_name}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-green-600">
                        {sku.count}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-400">
                      No SKU data for this session
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {previewImage && (
        <ImagePreviewModal
          imageUrl={previewImage.url}
          skuName={previewImage.name}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </div>
  );
};

const PAGE_SIZE = 5;

const WarehouseReport = () => {
  const dispatch = useDispatch();
  const roleName = localStorage.getItem("role_name");
  const filters = useSelector((state) => state.filters);

  const isVisoAdmin = roleName === VISO_SUPER_ADMIN;
  const localCompanyId = localStorage.getItem("company_id");
  const companyId = isVisoAdmin
    ? filters.selectedCompanyId || localCompanyId
    : localCompanyId;

  const {
    warehouseOptions,
    gateOptions,
    warehouseLoaded,
    sessionsData,
    isLoading,
  } = useSelector((state) => state.report);

  const [activeVideo, setActiveVideo] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [generatingReportId, setGeneratingReportId] = useState(null);

  // ---- Pagination state ----
  const [currentPage, setCurrentPage] = useState(1);

  const totalRows = sessionsData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const paginatedSessions = sessionsData.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );
  const startRow = totalRows === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endRow = Math.min(currentPage * PAGE_SIZE, totalRows);

  // Reset to page 1 whenever the underlying data changes (new filter results)
  useEffect(() => {
    setCurrentPage(1);
  }, [sessionsData]);

  useEffect(() => {
    if (!companyId) return;
    if (warehouseLoaded) return;

    dispatch(fetchWarehouseReportData({ companyId })).catch((err) => {
      errorMessage(getErrorMessage(err) || "Error fetching warehouse data.");
    });
  }, [companyId, warehouseLoaded]);

  const handleSubmit = (data) => {
    dispatch(
      setFilters({
        ...filters,
        yards: data.yards,
        gates: data.gates || [],
        startDate: data.startDate,
        endDate: data.endDate,
        filterMode: "date",
      }),
    );

    dispatch(
      fetchWarehouseSessions({
        gate_ids: data.gates,
        start_date: formatDateToISOString(data.startDate, true),
        end_date: formatDateToISOString(data.endDate, false),
      }),
    )
      .unwrap()
      .catch((err) => {
        errorMessage(getErrorMessage(err) || "Error fetching sessions.");
      });
  };

  const handleDownloadReport = async (session) => {
    setGeneratingReportId(session.session_id);
    try {
      const res = await getSessionSkusDetails(session.session_id);
      const skuDetails = res.status && Array.isArray(res.data) ? res.data : [];
      await generateSessionReportPDF(session, skuDetails);
    } catch (err) {
      errorMessage(getErrorMessage(err) || "Failed to generate report.");
    } finally {
      setGeneratingReportId(null);
    }
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <>
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader color="primary" size={40} />
        </div>
      ) : (
        <>
          <HeaderForm
            yardOptions={warehouseOptions}
            siteLabel="Warehouse"
            gateOptions={gateOptions}
            showGateDropdown
            onSubmit={handleSubmit}
            isFilterMode={false}
          />

          <div className="p-6 mt-4 bg-white rounded-2xl shadow-[0_2px_14px_rgba(15,28,53,0.07)] overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-sm border border-t-0 font-medium text-gray-500">Session ID</th>
                  <th className="px-4 py-3 text-sm border border-t-0 font-medium text-gray-500">Gate</th>
                  <th className="px-4 py-3 text-sm border border-t-0 font-medium text-gray-500">Truck No</th>
                  <th className="px-4 py-3 text-sm border border-t-0 font-medium text-gray-500">Task</th>
                  <th className="px-4 py-3 text-sm border border-t-0 font-medium text-gray-500">Start Time</th>
                  <th className="px-4 py-3 text-sm border border-t-0 font-medium text-gray-500">End Time</th>
                  <th className="px-4 py-3 text-sm border border-t-0 font-medium text-gray-500">Cargo</th>
                  <th className="px-4 py-3 text-sm border border-t-0 font-medium text-gray-500">SKU</th>
                  <th className="px-4 py-3 text-sm border border-t-0 font-medium text-gray-500">Status</th>
                  <th className="px-4 py-3 text-sm border border-t-0 font-medium text-gray-500">Video</th>
                  <th className="px-4 py-3 text-sm border border-t-0 font-medium text-gray-500">Report</th>
                </tr>
              </thead>
              <tbody className="divide-y text-center">
                {paginatedSessions.length > 0 ? (
                  paginatedSessions.map((s) => (
                    <tr key={s.session_id} className="hover:bg-gray-50 transition-colors">
                      <td
                        onClick={() => setSelectedSession(s)}
                        className="border border-t-0 px-4 py-3 text-sm text-blue-600 font-medium cursor-pointer"
                      >
                        #{s.session_id}
                      </td>
                      <td
                        onClick={() => setSelectedSession(s)}
                        className="border border-t-0 px-4 py-3 text-sm cursor-pointer"
                      >
                        {s.gate_name}
                      </td>
                      <td
                        onClick={() => setSelectedSession(s)}
                        className="border border-t-0 px-4 py-3 text-sm cursor-pointer"
                      >
                        {s.truck_number || "-"}
                      </td>
                      <td
                        onClick={() => setSelectedSession(s)}
                        className="border border-t-0 px-4 py-3 text-sm cursor-pointer"
                      >
                        {s.task || "-"}
                      </td>
                      <td
                        onClick={() => setSelectedSession(s)}
                        className="border border-t-0 px-4 py-3 text-sm cursor-pointer"
                      >
                        {s.start_time
                          ? new Date(s.start_time).toLocaleString("en-US", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })
                          : "-"}
                      </td>
                      <td
                        onClick={() => setSelectedSession(s)}
                        className="border border-t-0 px-4 py-3 text-sm cursor-pointer"
                      >
                        {s.end_time
                          ? new Date(s.end_time).toLocaleString("en-US", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })
                          : "-"}
                      </td>
                      <td
                        onClick={() => setSelectedSession(s)}
                        className="border border-t-0 px-4 py-3 text-sm cursor-pointer"
                      >
                        {s.cargo_count}
                      </td>
                      <td
                        onClick={() => setSelectedSession(s)}
                        className="border border-t-0 px-4 py-3 text-sm cursor-pointer"
                      >
                        {s.sku_type_count > 0 ? (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">
                            {s.sku_type_count} type{s.sku_type_count > 1 ? "s" : ""}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td
                        onClick={() => setSelectedSession(s)}
                        className="border border-t-0 px-4 py-3 text-sm capitalize cursor-pointer"
                      >
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full ${
                            s.status === "active"
                              ? "bg-red-100 text-red-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {s.status}
                        </span>
                      </td>
                      <td className="border border-t-0 px-4 py-3 text-sm">
                        {s.video_path ? (
                          <button
                            onClick={() => setActiveVideo(s.video_path)}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
                            title="View video"
                          >
                            <PlayCircle size={16} />
                            View
                          </button>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="border border-t-0 px-4 py-3 text-sm">
                        <button
                          onClick={() => handleDownloadReport(s)}
                          disabled={generatingReportId === s.session_id}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Download PDF report"
                        >
                          {generatingReportId === s.session_id ? (
                            <span
                              style={{
                                width: 14,
                                height: 14,
                                border: "2px solid #ef4444",
                                borderTopColor: "transparent",
                                borderRadius: "50%",
                                display: "inline-block",
                                animation: "spin 0.7s linear infinite",
                              }}
                            />
                          ) : (
                            <FileText size={16} />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={11} className="border px-4 py-8 text-center text-gray-500">
                      No sessions found for this filter
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* ---- Pagination controls ---- */}
            {totalRows > 0 && (
              <div className="flex items-center justify-between px-1 pt-4 text-sm text-gray-500">
                <span>
                  Showing <span className="font-medium text-gray-700">{startRow}</span>–
                  <span className="font-medium text-gray-700">{endRow}</span> of{" "}
                  <span className="font-medium text-gray-700">{totalRows}</span> sessions
                </span>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Previous page"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(
                      (p) =>
                        p === 1 ||
                        p === totalPages ||
                        Math.abs(p - currentPage) <= 1,
                    )
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push("ellipsis-" + p);
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p) =>
                      typeof p === "string" ? (
                        <span key={p} className="px-1.5 text-gray-400">
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => goToPage(p)}
                          className={`inline-flex items-center justify-center w-8 h-8 rounded-md text-sm font-medium transition-colors ${
                            p === currentPage
                              ? "bg-blue-600 text-white"
                              : "text-gray-600 hover:bg-gray-50 border border-gray-200"
                          }`}
                        >
                          {p}
                        </button>
                      ),
                    )}

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Next page"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {selectedSession && (
            <SessionDetailModal
              session={selectedSession}
              onClose={() => setSelectedSession(null)}
            />
          )}

          {activeVideo && (
            <VideoModal videoPath={activeVideo} onClose={() => setActiveVideo(null)} />
          )}
        </>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

export default WarehouseReport;